import { Component, ElementRef, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MatCheckboxChange,
  MatCheckboxModule,
} from '@angular/material/checkbox';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TemplateApiService } from 'src/app/api/services/template-api.service';
import { PermissionService } from 'src/app/auth/services/permission.service';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { DISPLAY_ACCESS } from 'src/app/auth/types/access-level';
import { ComponentsModule } from 'src/app/components/components.module';
import { ActionsMenu } from 'src/app/components/common-actions/common-actions.component';
import { ModalService } from 'src/app/components/modals/modal.service';
import { FeaturesService } from 'src/app/components/plans/features.service';
import { RvSpinnerDirective } from 'src/app/components/loading/rv-spinner.directive';
import { TemplatesService } from 'src/app/presentations/services/templates.service';
import {
  CapAlertListField,
  CapAlertsService,
} from 'src/app/settings/services/integrations/component-integrations/cap-alerts/cap-alerts.service';
import { BaseEditableComponent } from 'src/app/shared/components/base-editable/base-editable.component';
import { SharedModule } from 'src/app/shared/shared.module';
import { ClipboardService } from 'src/app/shared/services/clipboard.service';
import { environment } from 'src/environments/environment';
import { AriaCheckedDirective } from 'src/app/accessibility/directives/aria-checked/aria-checked.directive';
import { FocusOutlineDirective } from 'src/app/accessibility/directives/focus-outline/focus-outline.directive';

@Component({
  selector: 'cap-alerts',
  templateUrl: './cap-alerts.component.html',
  styleUrl: './cap-alerts.component.scss',
  standalone: true,
  imports: [
    FormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDialogModule,
    MatExpansionModule,
    MatIconModule,
    MatSlideToggleModule,
    MatTooltipModule,
    ComponentsModule,
    SharedModule,
    RvSpinnerDirective,
    FocusOutlineDirective,
    AriaCheckedDirective,
  ],
})
export class CapAlertsComponent
  extends BaseEditableComponent<any>
  implements OnInit
{
  ALERTS_WS_URL = environment.ALERTS_WS_URL;
  showFilters: boolean = false;
  featureAvailable: boolean | null = false;
  awaitingExportResponse: boolean = false;
  templates: any[] = [];
  actions: ActionsMenu = {};
  /** True only while persisting settings — not during template load. */
  saving = false;

  WARNING_MISSING_TEMPLATE = 'Please select an alerts template.';

  get alertUrl() {
    return this.ALERTS_WS_URL + '?id=' + this.capAlertsService.alertKey;
  }

  allowedSendersCsv = '';
  allowedHandlingCodesCsv = '';
  allowedEventCodesCsv = '';

  constructor(
    public capAlertsService: CapAlertsService,
    private featuresService: FeaturesService,
    private templatesService: TemplatesService,
    private templateApiService: TemplateApiService,
    protected modalService: ModalService,
    private clipboardService: ClipboardService,
    public userStateService: UserStateService,
    private permissionService: PermissionService,
    private companyStateService: CompanyStateService,
    private dialogRef: MatDialogRef<CapAlertsComponent>,
    private elementRef: ElementRef<HTMLElement>,
  ) {
    super();
  }

  get fieldsDisabled(): boolean {
    return !this.featureAvailable || !this.userStateService.hasPermission('ua');
  }

  get showCommonActions(): boolean {
    return this.hasVisibleNonCancelActions();
  }

  get saveButtonDisabled(): boolean {
    return (
      this.capAlertsService.isLoading ||
      !this.featureAvailable ||
      (this.capAlertsService.missingTemplate &&
        this.capAlertsService.alertSettings.templateProductCode === '')
    );
  }

  get subcompanyDistributeToAllAvailable(): boolean {
    return (
      this.capAlertsService.alertSettings.distributeToAll &&
      !this.companyStateService.isRootCompanySelected()
    );
  }

  get isCustomPresentationSelected(): boolean {
    return !!(
      this.capAlertsService.alertSettings.presentationId &&
      !this.capAlertsService.alertSettings.templateProductCode
    );
  }

  ngOnInit(): void {
    this.capAlertsService.loadSettings();
    this.featureAvailable = this.featuresService.isFeatureAvailable(
      FeaturesService.FEATURE_ALERTS,
    );

    this.allowedSendersCsv = this.capAlertsService.alertSettings.allowedSenders
      ? this.capAlertsService.alertSettings.allowedSenders.join(',')
      : '';
    this.allowedHandlingCodesCsv = this.capAlertsService.alertSettings
      .allowedHandlingCodes
      ? this.capAlertsService.alertSettings.allowedHandlingCodes.join(',')
      : '';
    this.allowedEventCodesCsv = this.capAlertsService.alertSettings
      .allowedEventCodes
      ? this.capAlertsService.alertSettings.allowedEventCodes.join(',')
      : '';

    this.actions = {
      saveAction: {
        id: 'saveButton',
        label: 'Save',
        action: () => this.saveSettings(),
        disabled: this.saveButtonDisabled,
        requireRole: 'ua',
      },
      cancelAction: {
        id: 'cancelButton',
        label: 'Cancel',
        action: () => this.cancel(),
      },
      moreOptions: [
        {
          id: 'exportButton',
          label: 'Export Alert History',
          action: () => {
            this.exportAlerts();
          },
          requireAccess: DISPLAY_ACCESS.FULL_ACCESS,
        },
      ],
    };

    this._loadTemplates();
  }

  canDeactivate(): boolean {
    return this.isDeepEqual(
      this.oldObject,
      this.capAlertsService.alertSettings,
    );
  }

  private _setDefaultTemplate() {
    if (
      !this.capAlertsService.alertSettings.templateProductCode &&
      !this.capAlertsService.alertSettings.presentationId &&
      this.templates.length
    ) {
      this.capAlertsService.alertSettings.templateProductCode =
        this.templates[0].productCode;
    }
  }

  private async _loadTemplates() {
    if (this.templatesService.capAlerts?.length) {
      this.templates = this.templatesService.capAlerts;
    } else {
      this.capAlertsService.isLoading = true;
      this.syncSaveActionDisabled();
      try {
        const templatesResponse = await this.templateApiService.listTemplates({
          count: 10,
          filter: 'templateContentTypes:=Common Alert Protocol',
        });
        this.templatesService.capAlerts = templatesResponse.items;
        this.templates = templatesResponse.items;
      } finally {
        this.capAlertsService.isLoading = false;
        this.syncSaveActionDisabled();
      }
    }

    this.oldObject = this.cloneDeep(this.capAlertsService.alertSettings);
    this._setDefaultTemplate();
    this.syncSaveActionDisabled();
  }

  private syncSaveActionDisabled(): void {
    if (this.actions.saveAction) {
      this.actions.saveAction.disabled = this.saveButtonDisabled;
    }
  }

  async exportAlerts(): Promise<void> {
    const userEmail = this.userStateService.getUserEmail();

    if (!userEmail) {
      this.modalService.showMessage(
        'Error',
        'Unable to retrieve email address',
      );
      return;
    }

    const CANCELLED = 'cancelled';
    const msgPre =
      'An export file will be prepared and emailed to you at <b>' +
      userEmail +
      '</b> once ready.<br/> Please ensure your email is configured to accept emails from <b>no-reply@risevision.com</b>.';
    const msgPost =
      'The alert history export has been initiated. You will receive an email once it is ready.';

    try {
      try {
        await this.modalService.confirm(
          'Export alerts?',
          msgPre,
          'Export',
          'Cancel',
        );
      } catch (err) {
        if (!err || (err instanceof Error && err.message === '')) {
          throw new Error(CANCELLED);
        }
        throw err;
      }

      this.awaitingExportResponse = true;
      await this.capAlertsService.exportAlerts(userEmail);
      this.modalService.showMessage('Export initiated', msgPost);
    } catch (err) {
      if (err instanceof Error && err.message === CANCELLED) {
        return;
      }

      this.modalService.showMessage('Error', 'Unable to export at this time.');
      console.error(err);
    } finally {
      this.awaitingExportResponse = false;
    }
  }

  parseCsvList(csv: string, fieldName: CapAlertListField) {
    if (this.fieldsDisabled) {
      return;
    }

    if (!this.capAlertsService.alertSettings[fieldName]) {
      this.capAlertsService.alertSettings[fieldName] = [];
    }

    const list = this.capAlertsService.alertSettings[fieldName];
    const items = csv.trim() === '' ? [] : csv.split(',');
    list.splice(0, list.length);
    items.forEach((item) => {
      list.push(item.trim());
    });
  }

  getCheckboxList(fieldName: CapAlertListField, key: string) {
    return (
      this.capAlertsService.alertSettings[fieldName] &&
      this.capAlertsService.alertSettings[fieldName].indexOf(key) >= 0
    );
  }

  setCheckboxList(fieldName: CapAlertListField, key: string, value?: boolean) {
    if (this.fieldsDisabled) {
      return;
    }

    if (!this.capAlertsService.alertSettings[fieldName]) {
      this.capAlertsService.alertSettings[fieldName] = [];
    }

    const fieldValue = this.capAlertsService.alertSettings[fieldName];
    const index = fieldValue.indexOf(key);
    if ((value === undefined && index < 0) || value) {
      fieldValue.push(key);
    } else {
      fieldValue.splice(index, 1);
    }
  }

  async saveSettings(): Promise<void> {
    this.capAlertsService.clearMessages();
    this.scrollDialogContentToTop();

    this.capAlertsService.alertSettings.enabled = true;
    this.saving = true;
    try {
      await this.capAlertsService.save();
      this.syncSaveActionDisabled();
      if (this.capAlertsService.errorSaving) {
        this.scrollDialogContentToTop();
        return;
      }
      this.oldObject = this.cloneDeep(this.capAlertsService.alertSettings);
      this.bypassUnsavedChanges();
      this.dialogRef.close(true);
    } finally {
      this.saving = false;
    }
  }

  dismissError(): void {
    this.capAlertsService.clearMessages();
  }

  private scrollDialogContentToTop(): void {
    const content =
      this.elementRef.nativeElement.querySelector('mat-dialog-content');
    content?.scrollTo({ top: 0, behavior: 'smooth' });
  }

  cancel(): void {
    this.bypassUnsavedChanges();
    this.dialogRef.close(null);
  }

  copyAlertUrl(): Promise<void> {
    return this.clipboardService.copy(this.alertUrl);
  }

  async resetAlertUrl(): Promise<void> {
    if (!this.featureAvailable) {
      return;
    }

    try {
      await this.modalService.confirm(
        'Reset Web Service URL?',
        'If you reset your Web Service URL, you will need to provide the new URL to your CAP Alerts provider, otherwise your alerts will stop working.',
        'I Understand',
        'Cancel',
        'urlResetButton',
      );
      await this.capAlertsService.regenerateAlertKey();
    } catch {
      // User cancelled confirm dialog
    }
  }

  setTemplate(productCode: string) {
    if (this.fieldsDisabled) {
      return;
    }

    this.capAlertsService.alertSettings.presentationId = '';
    this.capAlertsService.alertSettings.presentationName = '';
    this.capAlertsService.alertSettings.templateProductCode =
      productCode || this.templates[0]?.productCode || '';
    this.syncSaveActionDisabled();
  }

  handleCustomPresentationSelection(
    event: MouseEvent | MatCheckboxChange,
  ): void {
    if (this.fieldsDisabled) {
      return;
    }

    if (event instanceof MouseEvent) {
      const target = event.target as Element;
      if (target.closest('button') || target.closest('mat-checkbox')) {
        return;
      }

      void this.setCustomPresentation(!this.isCustomPresentationSelected);
      return;
    }

    void this.setCustomPresentation(event.checked, event);
  }

  async setCustomPresentation(selected: boolean, change?: MatCheckboxChange) {
    if (selected) {
      const previousSelection = {
        templateProductCode:
          this.capAlertsService.alertSettings.templateProductCode,
        presentationId: this.capAlertsService.alertSettings.presentationId,
        presentationName: this.capAlertsService.alertSettings.presentationName,
      };

      this.capAlertsService.alertSettings.templateProductCode = '';
      this.capAlertsService.alertSettings.presentationId = '';
      this.capAlertsService.alertSettings.presentationName = '';
      const changedPresentation =
        await this.capAlertsService.changePresentation();
      if (!changedPresentation) {
        this.capAlertsService.alertSettings.templateProductCode =
          previousSelection.templateProductCode;
        this.capAlertsService.alertSettings.presentationId =
          previousSelection.presentationId;
        this.capAlertsService.alertSettings.presentationName =
          previousSelection.presentationName;
        if (change) {
          change.source.checked = this.isCustomPresentationSelected;
        }
      }
    } else {
      this.capAlertsService.alertSettings.presentationId = '';
      this.capAlertsService.alertSettings.presentationName = '';
      if (this.templates?.length) {
        this.capAlertsService.alertSettings.templateProductCode =
          this.templates[0].productCode;
      }
    }
    this.syncSaveActionDisabled();
  }

  onDefaultExpiryInput(event: any) {
    const value = parseFloat(event.target.value);
    if (value < 0 || isNaN(value)) {
      event.target.value = 0;
      this.capAlertsService.alertSettings.defaultExpiry = 0;
    } else {
      this.capAlertsService.alertSettings.defaultExpiry = value;
    }
  }

  onDefaultExpiryKeydown(event: KeyboardEvent) {
    if (
      event.key === '-' ||
      event.key === '+' ||
      event.key === 'e' ||
      event.key === 'E'
    ) {
      event.preventDefault();
    }
  }

  isMobileViewport(): boolean {
    return (
      typeof window !== 'undefined' &&
      window.matchMedia('(max-width: 768px)').matches
    );
  }

  private hasVisibleNonCancelActions(): boolean {
    const saveVisible =
      !!this.actions?.saveAction &&
      this.permissionService.meetsAccessRequirement(this.actions.saveAction);

    const moreOptionsVisible = this.actions?.moreOptions?.some(
      (action) =>
        !action.disabled &&
        this.permissionService.meetsAccessRequirement(action),
    );

    const deleteVisible =
      !!this.actions?.deleteAction &&
      !this.actions.deleteAction.disabled &&
      this.permissionService.meetsAccessRequirement(this.actions.deleteAction);

    return saveVisible || moreOptionsVisible || deleteVisible;
  }
}
